import type { Recipe, Language, InstructionStep, MealType, NutritionFactsPer100g, Ingredient, AnalyzePrepTimelineResponse } from '../types';
import { buildAutoTaggedRecipeMetadata, normalizeSuitableFor } from '../utils/recipeFilterEstimation';
import { normalizeDietTypes } from '../utils/dietTypes';
import { estimateLifestyleTags, normalizeLifestyleTags } from '../utils/lifestyleTags';
import { unifyUnit } from '../utils/unitConversion';
import { splitSlashAlternativesInIngredient } from '../utils/splitIngredientAlternatives';

const GCF_BASE_URL = '/api/mealpai-ai';

export interface ParseRecipeOptions {
    url?: string;
    userId?: string;
}

export interface InstagramOwner {
    username?: string;
    fullName?: string;
    profilePicUrl?: string;
    userId?: string;
    biography?: string;
    websites?: string[];
}

export interface FacebookOwner {
    pageSlug: string;
    pageUrl: string;
}

export type RecipeParseErrorCode = 'instagram_unavailable' | 'not_a_recipe' | 'inappropriate_content' | 'generic';

export class RecipeParseError extends Error {
    readonly code: RecipeParseErrorCode;
    readonly severity?: number;

    constructor(code: RecipeParseErrorCode, message?: string, severity?: number) {
        super(message || code);
        this.name = 'RecipeParseError';
        this.code = code;
        this.severity = severity;
    }
}

function parseApiErrorBody(raw: string): { error?: string; code?: string; severity?: number } {
    if (!raw.trim()) return {};
    try {
        const parsed = JSON.parse(raw) as { error?: string; code?: string; severity?: number };
        return typeof parsed === 'object' && parsed !== null ? parsed : { error: raw };
    } catch {
        return { error: raw };
    }
}

function toRecipeParseError(status: number, raw: string, sourceUrl?: string): RecipeParseError {
    const body = parseApiErrorBody(raw);
    const severity = typeof body.severity === 'number' ? body.severity : undefined;
    if (body.code === 'not_a_recipe' || body.code === 'inappropriate_content') {
        return new RecipeParseError(body.code, body.error, severity);
    }
    if (status === 422 && body.error) {
        return new RecipeParseError('generic', body.error);
    }
    const classified = classifyParseError(new Error(body.error || raw), sourceUrl);
    return new RecipeParseError(classified, body.error);
}

function normalizeInstagramOwner(value: unknown): InstagramOwner | undefined {
    if (!value || typeof value !== 'object') return undefined;
    const owner = value as Record<string, unknown>;
    const username = typeof owner.username === 'string' ? owner.username : undefined;
    const fullName = typeof owner.fullName === 'string' ? owner.fullName : undefined;
    const profilePicUrl = typeof owner.profilePicUrl === 'string' ? owner.profilePicUrl : undefined;
    const userId = typeof owner.userId === 'string' ? owner.userId.trim() : undefined;
    const biography = typeof owner.biography === 'string' ? owner.biography : undefined;
    const websites = Array.isArray(owner.websites)
        ? owner.websites.filter((item): item is string => typeof item === 'string' && item.trim().length > 0)
        : undefined;
    if (!username && !fullName && !profilePicUrl && !userId && !biography && !websites?.length) return undefined;
    return { username, fullName, profilePicUrl, userId, biography, websites };
}

function normalizeFacebookOwner(value: unknown): FacebookOwner | undefined {
    if (!value || typeof value !== 'object') return undefined;
    const owner = value as Record<string, unknown>;
    const pageSlug = typeof owner.pageSlug === 'string' ? owner.pageSlug.trim() : '';
    const pageUrl = typeof owner.pageUrl === 'string' ? owner.pageUrl.trim() : '';
    if (!pageSlug) return undefined;
    return { pageSlug, pageUrl: pageUrl || `https://www.facebook.com/${pageSlug}` };
}

async function callMealpaiAi(action: 'parse-recipe' | 'translate-recipe' | 'calculate-nutrition' | 'analyze-prep-timeline' | 'user-key/encrypt' | 'user-key/reveal' | 'instagram-profile' | 'resolve-recipe-source', payload: Record<string, unknown>): Promise<any> {
    const subpathUrl = `${GCF_BASE_URL}/${action}`;
    const body = { action, ...payload };

    try {
        const response = await fetch(subpathUrl, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(body),
        });

        if (response.ok) {
            return await response.json();
        }
        const details = await response.text().catch(() => '');
        console.warn(`GCF subpath returned status ${response.status}. Trying base URL instead.`);
        if (response.status === 422 && details) {
            throw toRecipeParseError(response.status, details);
        }
    } catch (e) {
        if (e instanceof RecipeParseError) throw e;
        console.warn('GCF subpath fetch failed:', e);
    }

    const response = await fetch(GCF_BASE_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
    });

    if (!response.ok) {
        const details = await response.text().catch(() => '');
        throw toRecipeParseError(response.status, details);
    }

    return await response.json();
}

function normalizeServings(value: unknown): number {
    const numeric = typeof value === 'number' ? value : Number(value);
    if (Number.isFinite(numeric) && numeric >= 1) {
        return Math.round(numeric);
    }
    return 4;
}

export type ParsedRecipeResult = Omit<Recipe, 'id'> & {
    instagramUsername?: string;
    instagramOwner?: InstagramOwner;
    facebookOwner?: FacebookOwner;
};

function normalizeParsedRecipe(parsed: Record<string, unknown>, targetLanguage: Language): ParsedRecipeResult {
    const instructions = ((parsed.instructions as unknown[]) || []).map((s: unknown) => {
        const step = s as Record<string, unknown>;
        return {
            ...step,
            id: (step.id as string) || `ai-${Math.random().toString(36).substr(2, 9)}`,
            type: (step.type as InstructionStep['type']) || 'step',
            content: (step.content as string) || '',
        };
    });

    const ingredients = ((parsed.ingredients as unknown[]) || []).map((ing: unknown) => {
        const item = ing as Record<string, unknown>;
        const alternatives = Array.isArray(item.alternatives)
            ? (item.alternatives as Record<string, unknown>[]).map((alt) => ({
                name: String(alt.name ?? ''),
                quantity: typeof alt.quantity === 'number' ? alt.quantity : Number(alt.quantity) || 0,
                unit: unifyUnit(String(alt.unit ?? 'unit')),
                isMeat: Boolean(alt.isMeat),
            })).filter((alt) => alt.name.trim().length > 0)
            : undefined;
        return splitSlashAlternativesInIngredient({
            ...item,
            unit: unifyUnit((item.unit as string) || 'unit'),
            ...(alternatives && alternatives.length > 0 ? { alternatives } : {}),
        } as Ingredient);
    });

    const prepTime = (parsed.prepTime as number) || 20;
    const category = parsed.category as Recipe['category'];
    const name = (parsed.name as string) || '';
    const autoTagInput = {
        name,
        prepTime,
        ingredients: ingredients as Ingredient[],
        category,
        instructions,
        suitableFor: normalizeSuitableFor(parsed.suitableFor),
        dietTypes: normalizeDietTypes(parsed.dietTypes, parsed.isKeto as boolean | undefined),
        isKeto: parsed.isKeto as boolean | undefined,
    };
    const autoTagged = buildAutoTaggedRecipeMetadata(autoTagInput);

    return {
        ...(parsed as ParsedRecipeResult),
        name,
        servings: normalizeServings(parsed.servings),
        prepTime,
        ingredients: ingredients as Ingredient[],
        instructions,
        links: Array.isArray(parsed.links) ? parsed.links as string[] : [],
        suitableFor: autoTagged.suitableFor,
        dietTypes: autoTagged.dietTypes,
        category: autoTagged.category,
        lifestyleTags: normalizeLifestyleTags(parsed.lifestyleTags ?? estimateLifestyleTags({
            ...autoTagInput,
            suitableFor: autoTagged.suitableFor,
        })),
        originLanguage: (parsed.originLanguage as Language) || (parsed.detectedLanguage as Language) || targetLanguage,
        instagramUsername: typeof parsed.instagramUsername === 'string' ? parsed.instagramUsername : undefined,
        instagramOwner: normalizeInstagramOwner(parsed.instagramOwner),
        facebookOwner: normalizeFacebookOwner(parsed.facebookOwner),
        externalVideoId: typeof parsed.externalVideoId === 'string' ? parsed.externalVideoId.trim() : undefined,
        externalVideoIdAliases: Array.isArray(parsed.externalVideoIdAliases)
            ? parsed.externalVideoIdAliases.filter((alias): alias is string => typeof alias === 'string' && alias.trim().length > 0)
            : undefined,
    };
}

const INSTAGRAM_URL_PATTERN = /instagram\.com|instagr\.am/i;
const INSTAGRAM_ERROR_PATTERN = /instagram|scraper|rapidapi/i;

function classifyParseError(error: unknown, sourceUrl?: string): RecipeParseErrorCode {
    const raw = error instanceof Error ? error.message : typeof error === 'string' ? error : '';
    if ((sourceUrl && INSTAGRAM_URL_PATTERN.test(sourceUrl)) || INSTAGRAM_ERROR_PATTERN.test(raw)) {
        return 'instagram_unavailable';
    }
    return 'generic';
}

export const parseRecipeWithGemini = async (
    recipeText: string,
    targetLanguage: Language = 'en',
    options?: ParseRecipeOptions,
    encryptedGeminiKey?: string
): Promise<ParsedRecipeResult> => {
    try {
        const payload: Record<string, unknown> = { targetLanguage };
        const sourceUrl = options?.url?.trim();

        if (sourceUrl) {
            payload.url = sourceUrl;
        }

        const trimmedRecipeText = recipeText.trim();
        if (trimmedRecipeText) {
            payload.recipeText = trimmedRecipeText;
            payload.text = trimmedRecipeText;
            payload.recipe = trimmedRecipeText;
        } else if (!sourceUrl) {
            payload.recipeText = recipeText;
            payload.text = recipeText;
            payload.recipe = recipeText;
        }

        if (encryptedGeminiKey) {
            payload.userEncryptedKey = encryptedGeminiKey;
        }

        if (options?.userId) {
            payload.userId = options.userId;
        }

        const parsed = await callMealpaiAi('parse-recipe', payload);
        if (!parsed) throw new Error('AI returned empty result.');

        return normalizeParsedRecipe(parsed, targetLanguage);
    } catch (error: unknown) {
        console.error('Gemini GCF Error:', error);
        if (error instanceof RecipeParseError) throw error;
        throw new RecipeParseError(classifyParseError(error, options?.url));
    }
};

export function getRecipeParseErrorMessage(
    error: unknown,
    t: (key: 'analyzeFailed' | 'scraperUnavailableMsg' | 'recipeNotRecipeMsg' | 'recipeInappropriateMsg') => string,
): string {
    if (error instanceof RecipeParseError) {
        if (error.code === 'instagram_unavailable') return t('scraperUnavailableMsg');
        if (error.code === 'not_a_recipe') return error.message || t('recipeNotRecipeMsg');
        if (error.code === 'inappropriate_content') return error.message || t('recipeInappropriateMsg');
        if (error.message && error.message !== error.code) return error.message;
    }
    return t('analyzeFailed');
}

export const generateEnglishTranslation = async (
    name: string,
    instructions: InstructionStep[],
    ingredients: { name: string; unit: string; section?: string }[],
    encryptedGeminiKey?: string
): Promise<Recipe['englishTranslation']> => {
    try {
        const parsed = await callMealpaiAi('translate-recipe', {
            name,
            instructions: instructions.map((s) => ({ type: s.type, content: s.content })),
            ingredients,
            ...(encryptedGeminiKey ? { userEncryptedKey: encryptedGeminiKey } : {}),
        });

        if (!parsed) return undefined;

        if (parsed.instructions) {
            parsed.instructions = parsed.instructions.map((s: any, i: number) => ({
                ...s,
                id: instructions[i]?.id || s.id || `trans-${Math.random()}`,
            }));
        }
        return parsed;
    } catch (error) {
        console.warn('Translation via AI service failed (recipe saved without English translation):', error);
        return undefined;
    }
};

function normalizeNutritionFacts(raw: Record<string, unknown>): NutritionFactsPer100g | undefined {
    const num = (key: string) => {
        const v = raw[key];
        return typeof v === 'number' && Number.isFinite(v) ? v : Number(v);
    };

    const calories = num('calories');
    const fatGrams = num('fatGrams');
    const fatPercentage = num('fatPercentage');
    const sodiumMg = num('sodiumMg');
    const dietaryFiberGrams = num('dietaryFiberGrams');
    const totalSugarsGrams = num('totalSugarsGrams');
    const addedSugarsGrams = num('addedSugarsGrams');
    const proteinGrams = num('proteinGrams');

    if (![calories, fatGrams, fatPercentage, sodiumMg, dietaryFiberGrams, totalSugarsGrams, addedSugarsGrams, proteinGrams].every(Number.isFinite)) {
        return undefined;
    }

    return {
        calories: Math.round(calories),
        fatGrams: Math.round(fatGrams * 10) / 10,
        fatPercentage: Math.round(fatPercentage * 10) / 10,
        sodiumMg: Math.round(sodiumMg),
        dietaryFiberGrams: Math.round(dietaryFiberGrams * 10) / 10,
        totalSugarsGrams: Math.round(totalSugarsGrams * 10) / 10,
        addedSugarsGrams: Math.round(addedSugarsGrams * 10) / 10,
        proteinGrams: Math.round(proteinGrams * 10) / 10,
        calculatedAt: typeof raw.calculatedAt === 'string' ? raw.calculatedAt : new Date().toISOString(),
    };
}

export const calculateNutritionFacts = async (
    ingredients: Ingredient[],
    servings: number,
    originLanguage: Language = 'en',
    encryptedGeminiKey?: string
): Promise<NutritionFactsPer100g | undefined> => {
    try {
        const payload: Record<string, unknown> = {
            ingredients: ingredients.map(({ name, quantity, unit, isMeat, section }) => ({
                name,
                quantity,
                unit,
                isMeat,
                section,
            })),
            servings,
            originLanguage,
        };

        if (encryptedGeminiKey) {
            payload.userEncryptedKey = encryptedGeminiKey;
        }

        const result = await callMealpaiAi('calculate-nutrition', payload);
        if (!result) return undefined;
        return normalizeNutritionFacts(result);
    } catch (error) {
        console.warn('Nutrition calculation via AI service failed (recipe saved without nutrition facts):', error);
        return undefined;
    }
};

export function isRecipeUrl(value: string): boolean {
    return /^(https?:\/\/[^\s"]+)$/i.test(value.trim());
}

export async function resolveRecipeSourceUrl(url: string): Promise<{
    externalVideoId?: string;
    externalVideoIdAliases?: string[];
    resolvedUrl?: string;
}> {
    const parsed = await callMealpaiAi('resolve-recipe-source', { url: url.trim() });
    if (!parsed || typeof parsed !== 'object') return {};
    const record = parsed as Record<string, unknown>;
    return {
        externalVideoId: typeof record.externalVideoId === 'string' ? record.externalVideoId : undefined,
        externalVideoIdAliases: Array.isArray(record.externalVideoIdAliases)
            ? record.externalVideoIdAliases.filter((alias): alias is string => typeof alias === 'string' && alias.trim().length > 0)
            : undefined,
        resolvedUrl: typeof record.resolvedUrl === 'string' ? record.resolvedUrl : undefined,
    };
}

/**
 * Sends a plain Gemini API key to the AI service for server-side encryption.
 * Returns the encrypted token safe to store in the user's Firestore document.
 */
export async function encryptGeminiKey(plainKey: string): Promise<string> {
    const result = await callMealpaiAi('user-key/encrypt', { plainKey });
    if (!result?.encryptedKey) throw new Error('Encryption service returned an empty response.');
    return result.encryptedKey as string;
}

/**
 * Sends an encrypted Gemini key to the AI service for server-side decryption.
 * Returns the original plain key for temporary display only.
 */
export async function revealGeminiKey(encryptedKey: string): Promise<string> {
    const result = await callMealpaiAi('user-key/reveal', { encryptedKey });
    if (!result?.plainKey) throw new Error('Decryption service returned an empty response.');
    return result.plainKey as string;
}

export type PrepTimelineAnalysisError = 'service_unavailable' | 'invalid_user_key' | 'empty_response' | 'rate_limited';

export async function analyzePrepTimeline(
    recipes: { id: string; name: string; prepTime?: number; instructions: Recipe['instructions'] }[],
    targetLanguage: Language = 'en',
    encryptedGeminiKey?: string
): Promise<{ data?: AnalyzePrepTimelineResponse; error?: PrepTimelineAnalysisError }> {
    if (!recipes.length) return { data: { recipes: [] } };

    const payload: Record<string, unknown> = {
        recipes: recipes.map((recipe) => ({
            id: recipe.id,
            name: recipe.name,
            prepTime: recipe.prepTime,
            instructions: recipe.instructions,
        })),
        targetLanguage,
    };

    if (encryptedGeminiKey) {
        payload.userEncryptedKey = encryptedGeminiKey;
    }

    const subpathUrl = `${GCF_BASE_URL}/analyze-prep-timeline`;
    const body = { action: 'analyze-prep-timeline', ...payload };

    const parseFailure = (status: number, details: string): PrepTimelineAnalysisError => {
        if (status === 429) return 'rate_limited';
        if (status === 400 && details.includes('Invalid user API key')) return 'invalid_user_key';
        return 'service_unavailable';
    };

    try {
        let response = await fetch(subpathUrl, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(body),
        });

        if (!response.ok) {
            const details = await response.text().catch(() => '');
            if (response.status === 422 && details) {
                throw toRecipeParseError(response.status, details);
            }
            if (response.status !== 404) {
                return { error: parseFailure(response.status, details) };
            }

            response = await fetch(GCF_BASE_URL, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(body),
            });
            if (!response.ok) {
                const fallbackDetails = await response.text().catch(() => '');
                return { error: parseFailure(response.status, fallbackDetails || details) };
            }
        }

        const result = await response.json();
        if (!result || !Array.isArray(result.recipes) || result.recipes.length === 0) {
            return { error: 'empty_response' };
        }
        return { data: result as AnalyzePrepTimelineResponse };
    } catch (error) {
        if (error instanceof RecipeParseError) throw error;
        console.warn('Prep timeline analysis via AI service failed:', error);
        return { error: 'service_unavailable' };
    }
}

export interface InstagramProfileLookup {
    username?: string;
    userId?: string;
}

export async function fetchInstagramProfile(input: InstagramProfileLookup): Promise<InstagramOwner | undefined> {
    const username = input.username?.trim();
    const userId = input.userId?.trim();
    if (!username && !userId) return undefined;

    try {
        const profile = await callMealpaiAi('instagram-profile', {
            ...(username ? { username } : {}),
            ...(userId ? { userId } : {}),
        });
        return normalizeInstagramOwner(profile);
    } catch (error) {
        console.error('Instagram profile fetch failed:', error);
        return undefined;
    }
}

/** @deprecated Use fetchInstagramProfile */
export async function fetchInstagramProfileByUsername(username: string): Promise<InstagramOwner | undefined> {
    return fetchInstagramProfile({ username: username.trim() });
}
